// Single source of truth for navigation: which pages exist, their route, and which
// role-aware "app" group they belong to. The Sidebar renders from this, RoleManager
// grants permissions from this, and canAccess() gates on the ids here.
import {
  LayoutDashboard, Bell, Columns3, FileText, Receipt, UserPlus, Sparkles,
  Building2, CalendarDays, Hammer, DoorOpen, Package, BarChart3, ListTodo,
  Users, IndianRupee, AlertTriangle, FileSpreadsheet, PieChart, Fingerprint,
  Layers, Database, Contact, PhoneCall, TrendingUp, Wallet, LineChart, CalendarCheck2, HandCoins,
  LayoutTemplate, CalendarClock, PackageCheck, Footprints, Landmark, ShoppingCart, Factory, MessageSquare, PhoneOutgoing,
  AlarmClock, ClipboardList, LifeBuoy, Workflow, Zap, LayoutList, BookOpen, Images, BookLock, Palette, Truck, Link2, Compass,
} from "lucide-react";

// app: the switcher group · id: unique key · to: route · label/icon: display
// perm (optional): the page grant that gates this item when it differs from
// id — lets a new screen ride an existing grant (Follow-ups -> leads,
// Service -> projects, Catalogues -> quotes) so no existing account needs re-permissioning.
// costOnly: only for people who can see landing prices (admin, accounts, "Can see landing price").
export const NAV = [
  // Overview (pinned — always shown above the app switcher)
  // Start here (docs/GUIDANCE.md): rides the grants people already have, so nobody needs re-permissioning.
  { app: "overview", id: "start-here", perm: ["dashboard", "leads", "quotes", "visitors", "tasks", "calls", "customers",
    "attendance", "invoice-gen", "cashbook", "finance-payments", "inventory", "purchase-orders", "projects"],
    to: "/start", label: "Start here", icon: Compass, pinned: true },
  { app: "overview", id: "dashboard", to: "/", label: "Dashboard", icon: LayoutDashboard, pinned: true },
  { app: "overview", id: "analytics", to: "/analytics", label: "Analytics", icon: BarChart3, pinned: true },
  { app: "overview", id: "sales-tracker", perm: "analytics", to: "/sales-tracker", label: "Sales Tracker", icon: TrendingUp, pinned: true },
  { app: "overview", id: "alerts", to: "/alerts", label: "Follow-Up Alerts", icon: Bell, pinned: true },

  // Sell
  { app: "sell", id: "leads", to: "/leads", label: "Leads", icon: Sparkles },
  { app: "sell", id: "followups", perm: "leads", to: "/follow-ups", label: "Lead Follow-ups", icon: AlarmClock },
  { app: "sell", id: "calls", to: "/calls", label: "Call Log", icon: PhoneOutgoing },
  { app: "sell", id: "pipeline", to: "/pipeline", label: "Pipeline", icon: Columns3 },
  { app: "sell", id: "quotes", to: "/quotes", label: "Deals / Quotes", icon: FileText },
  { app: "sell", id: "quote-builder", to: "/quotes/builder", label: "Quote Builder", icon: LayoutTemplate },
  { app: "sell", id: "store-walk", perm: "quotes", to: "/quotes/walk", label: "Store Walk Quote", icon: Footprints },
  { app: "sell", id: "quote-followups", to: "/quotes/followups", label: "Quote Follow-ups", icon: PhoneCall },
  { app: "sell", id: "catalogues", perm: "quotes", to: "/catalogues", label: "Catalogues", icon: BookOpen },
  // The showroom's catalogue: whoever quotes, or meets visitors and leads (reception, sales).
  { app: "sell", id: "architect-studio", perm: ["quotes", "visitors", "leads"], to: "/architect-studio", label: "Architect Studio", icon: Palette },
  { app: "sell", id: "virtual-catalogue", perm: ["quotes", "visitors", "leads"], to: "/virtual-catalogue", label: "Virtual Catalogue", icon: Images },
  // Vendors' own brochures: landing-price holders only (costOnly), like the prices inside them.
  { app: "sell", id: "vendor-brochures", perm: "quotes", to: "/vendor-brochures", label: "Vendor Brochures", icon: BookLock, costOnly: true },
  { app: "sell", id: "sales", to: "/sales", label: "Sales Register", icon: Receipt },
  { app: "sell", id: "visitors", to: "/visitors", label: "Visitors", icon: UserPlus },

  // Deliver
  { app: "deliver", id: "projects", to: "/projects", label: "Projects", icon: Hammer },
  { app: "deliver", id: "delivery-challans", perm: "sales", to: "/delivery-challans", label: "Delivery Challans", icon: Truck },
  { app: "deliver", id: "service", perm: "projects", to: "/service", label: "Service & Warranty", icon: LifeBuoy },
  { app: "deliver", id: "dwsurvey", to: "/dw-survey", label: "Site Survey", icon: DoorOpen },
  { app: "deliver", id: "outstanding", to: "/outstanding", label: "Outstanding", icon: AlertTriangle },

  // Stock
  { app: "stock", id: "inventory", to: "/inventory", label: "Stock", icon: Package },
  { app: "stock", id: "stock-ledger", to: "/stock-ledger", label: "Stock Ledger", icon: Layers },
  { app: "stock", id: "purchase-orders", to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
  // Goods Receipts (docs/STOCK_FLOW.md): the store's steps for each issued PO; rides the inventory grant.
  { app: "stock", id: "goods-receipts", perm: "inventory", to: "/goods-receipts", label: "Goods Receipts", icon: PackageCheck },
  // Stock Counts (docs/STOCK_FLOW.md): count the shelves; stock is corrected once someone else approves.
  { app: "stock", id: "stock-counts", perm: "inventory", to: "/stock-counts", label: "Stock Counts", icon: ClipboardList },
  { app: "stock", id: "manufacturer-orders", to: "/manufacturer-orders", label: "Manufacturer Orders", icon: Factory },
  { app: "stock", id: "inv-analytics", to: "/inventory/analytics", label: "Inv. Analytics", icon: BarChart3 },

  // Money
  // The accounts team's home (who owes us, receipts due, money requests, wallets); "/" opens it for an accounts login.
  { app: "money", id: "accounts-home", perm: ["invoice-gen", "cashbook"], to: "/accounts", label: "Accounts Desk", icon: Landmark },
  { app: "money", id: "expenses", to: "/money-requests", label: "Money Requests", icon: HandCoins },
  { app: "money", id: "cashbook", to: "/cashbook", label: "Wallets & Cashbooks", icon: Wallet },
  { app: "money", id: "pnl", to: "/finance/pnl", label: "Profit & Loss", icon: TrendingUp },
  { app: "money", id: "project-pnl", to: "/reports/project-pnl", label: "Project P&L", icon: LineChart },
  { app: "money", id: "invoice-gen", to: "/invoices", label: "Tax Invoices", icon: FileSpreadsheet },
  { app: "money", id: "petty", to: "/petty-cash", label: "Petty Cash (history)", icon: IndianRupee },
  { app: "money", id: "incentives", to: "/incentives", label: "Incentives", icon: HandCoins },
  { app: "money", id: "finance-payments", to: "/payments", label: "Payments & Tax Invoices", icon: Landmark },

  // Relations
  { app: "relations", id: "customers", to: "/customers", label: "Customers", icon: Contact },
  { app: "relations", id: "architects", to: "/architects", label: "Architects", icon: Building2 },
  { app: "relations", id: "meetplan", to: "/meets", label: "Meet Planner", icon: CalendarDays },

  // Command
  { app: "command", id: "executive", to: "/executive", label: "Executive Analytics", icon: TrendingUp },
  { app: "command", id: "reports", to: "/reports", label: "Reports", icon: PieChart },
  { app: "command", id: "tasks", to: "/tasks", label: "Tasks", icon: ListTodo },
  { app: "command", id: "daily-planner", to: "/daily-planner", label: "Daily Planner", icon: CalendarCheck2 },
  // Work Scheduler (docs/WORK_SCHEDULER.md): everyone's day by hour for admin / team managers; rides the tasks grant.
  { app: "command", id: "work-schedule", perm: "tasks", to: "/work-schedule", label: "Work Scheduler", icon: CalendarClock },
  { app: "command", id: "attendance", to: "/attendance", label: "Attendance", icon: Fingerprint },
  { app: "command", id: "payroll", to: "/people/payroll", label: "Payroll", icon: IndianRupee },
  { app: "command", id: "data-centre", to: "/data-centre", label: "Data Centre", icon: Database },
  { app: "command", id: "discussions", to: "/discussions", label: "Discussion Board", icon: MessageSquare },
  { app: "command", id: "record-chain", to: "/record-chain", label: "Record Chain", icon: TrendingUp },
  { app: "command", id: "audit-trail", to: "/audit", label: "Audit Trail", icon: AlertTriangle, adminOnly: true },
  { app: "command", id: "roles", to: "/admin/roles", label: "Role Manager", icon: Users, adminOnly: true },
  { app: "command", id: "workflows", to: "/admin/workflows", label: "Workflows", icon: Workflow, adminOnly: true },
  { app: "command", id: "flows", to: "/admin/flows", label: "Flows", icon: Zap, adminOnly: true },
  { app: "command", id: "forms", to: "/admin/forms", label: "Form Builder", icon: LayoutList, adminOnly: true },
  { app: "command", id: "go-live", to: "/admin/go-live", label: "Go-live Data", icon: Database, adminOnly: true },
  { app: "command", id: "data-check", to: "/admin/data-check", label: "Data check", icon: Link2, adminOnly: true },
];

// Flat list for the Role Manager permission grid.
export const ALL_PAGES = NAV.filter((n) => (!n.adminOnly || n.id === "roles") && !n.perm)
  .map((n) => ({ id: n.id, label: n.label }));

// Dashboard isn't granted to every Role (it mixes company-wide numbers a
// scoped rep shouldn't see). So landing there after login is wrong for them —
// this picks the first page in NAV order they can actually open, for
// ProtectedRoute to send them to instead of a blank "No access" screen.
// Returns null when nothing is accessible at all (e.g. no Role yet).
export function firstAccessiblePath(canAccess, isAdmin) {
  for (const item of NAV) {
    if (item.id === "dashboard" || item.id === "start-here") continue;     // Start here is a guide, not a work page
    if (item.adminOnly && !isAdmin) continue;
    if (canAccess(item.perm || item.id)) return item.to;
  }
  return null;
}

// A specific launch page an admin chose for someone in Role Manager
// (users.landing_page, a NAV id). Returns the route only if that page still
// exists and the person can still open it — a Role change since it was set
// doesn't leave them stuck, it's just not honored, same as a removed grant.
export function landingPagePath(pageId, canAccess) {
  if (!pageId) return null;
  const item = NAV.find((n) => n.id === pageId);
  if (!item || !canAccess(item.perm || item.id)) return null;
  return item.to;
}
